import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { useGetPrintersQuery } from '../services/api/PrinterManager';
import { useAuth } from '../providers/AuthProvider';
import useNativeApp from './useNativeApp';

export default function usePrinters() {
  const { isLoggedIn } = useAuth() ?? {};

  const { supportsPrinterBridge } = useNativeApp();
  // Narrow selector rather than the whole slice: every printer consumer subscribes through this
  // hook, and the profile object is replaced wholesale on each refresh.
  const hasExternalPrinterFeature = useSelector((state) =>
    Boolean(state.company.company?.has_external_printer_feature),
  );

  // The single answer to "is the external printer usable here" — the company must be entitled to
  // it and the shell must speak the bridge. It gates the list request, the connect probe, the
  // settings UI and print routing alike, so those four can never disagree. Absent flag means off:
  // the profile response is the only source, and a missing field is not an entitlement.
  const externalPrinterEnabled = hasExternalPrinterFeature && supportsPrinterBridge;

  const {
    data: printers,
    isFetching,
    refetch,
  } = useGetPrintersQuery(undefined, {
    // The list exists to serve external printers, so there is nothing to fetch while they are off.
    skip: !isLoggedIn || !externalPrinterEnabled,
    // Subscription option, not an endpoint one — RTK Query only reads it from here or
    // from the createApi config. Earns its keep in the browser build; inside the native
    // shell it is redundant, because losing the connection unmounts the WebView entirely
    // and reconnecting remounts it into a fresh page load.
    refetchOnReconnect: true,
  });

  const { externalPrinterConnected } = useSelector((state) => state.print);

  const defaultPrinter = useMemo(() => collect(printers ?? []).firstWhere('is_default', true), [printers]);

  // Gated as well, not just derived from it: native can report a connection unprompted, and a
  // printer left flagged `is_default` from an entitled period must not keep pulling jobs.
  const printsExternally = externalPrinterEnabled && Boolean(defaultPrinter && externalPrinterConnected);

  return useMemo(
    () => ({
      printers,
      defaultPrinter,
      externalPrinterEnabled,
      printsExternally,
      isFetching,
      refetch,
    }),
    [printers, defaultPrinter, externalPrinterEnabled, printsExternally, isFetching, refetch],
  );
}
